/**
 * Shared merge helpers for flow breakdown modes (conversion + cohorts).
 * Used by FlowVisualization and flowDisplayPrep.
 */

export const COHORT_PALETTE = [
  "#6366f1",
  "#14b8a6",
  "var(--feedback-warning)",
  "#ec4899",
  "#8b5cf6",
  "var(--feedback-success)",
  "#06b6d4",
  "#f97316",
];

export function cohortColorsByName(names) {
  const m = {};
  (names || []).forEach((n, i) => {
    m[n] = COHORT_PALETTE[i % COHORT_PALETTE.length];
  });
  return m;
}

export const SEG_CONVERTED = "Converted";
export const SEG_DID_NOT = "Did not convert";
export const SEG_ORDER = [SEG_CONVERTED, SEG_DID_NOT];

export function mergeConversionNodes(convNodes, dncNodes) {
  const map = new Map();
  for (const n of convNodes) {
    map.set(n.id, {
      ...n,
      segmentCounts: { [SEG_CONVERTED]: n.count, [SEG_DID_NOT]: 0 },
      count: n.count,
    });
  }
  for (const n of dncNodes) {
    const ex = map.get(n.id);
    if (ex) {
      ex.segmentCounts[SEG_DID_NOT] = n.count;
      ex.count = (ex.segmentCounts[SEG_CONVERTED] || 0) + (ex.segmentCounts[SEG_DID_NOT] || 0);
    } else {
      map.set(n.id, {
        ...n,
        segmentCounts: { [SEG_CONVERTED]: 0, [SEG_DID_NOT]: n.count },
        count: n.count,
      });
    }
  }
  return [...map.values()];
}

export function recomputeConversionMergedPercentages(nodes, anchors) {
  const step0 = anchors?.[0]?.step;
  if (step0 === undefined || step0 === null || !nodes.length) return nodes;
  const cohort = nodes.find((n) => n.isAnchor && n.step === step0)?.count ?? 0;
  if (cohort <= 0) return nodes;
  return nodes.map((n) => ({
    ...n,
    percentage: parseFloat((((n.count || 0) / cohort) * 100).toFixed(1)),
  }));
}

export function mergeConversionLinks(convLinks, dncLinks) {
  const key = (l) => `${l.source}::${l.target}`;
  const map = new Map();
  for (const l of convLinks) {
    map.set(key(l), {
      ...l,
      segmentValues: { [SEG_CONVERTED]: l.value, [SEG_DID_NOT]: 0 },
      value: l.value,
    });
  }
  for (const l of dncLinks) {
    const k = key(l);
    const ex = map.get(k);
    if (ex) {
      ex.segmentValues[SEG_DID_NOT] = l.value;
      ex.value = (ex.segmentValues[SEG_CONVERTED] || 0) + (ex.segmentValues[SEG_DID_NOT] || 0);
    } else {
      map.set(k, {
        ...l,
        segmentValues: { [SEG_CONVERTED]: 0, [SEG_DID_NOT]: l.value },
        value: l.value,
      });
    }
  }
  return [...map.values()];
}

export function mergeCohortNodes(cohortOrder, dataByCohort) {
  const map = new Map();
  const emptySeg = () => Object.fromEntries(cohortOrder.map((c) => [c, 0]));

  for (const cohortName of cohortOrder) {
    const nodeList = dataByCohort[cohortName]?.nodes || [];
    for (const n of nodeList) {
      const ex = map.get(n.id);
      if (!ex) {
        map.set(n.id, {
          ...n,
          segmentCounts: { ...emptySeg(), [cohortName]: n.count },
          count: n.count,
        });
      } else {
        ex.segmentCounts[cohortName] = (ex.segmentCounts[cohortName] || 0) + n.count;
        ex.count = cohortOrder.reduce((s, cn) => s + (ex.segmentCounts[cn] || 0), 0);
      }
    }
  }
  return [...map.values()];
}

export function recomputeCohortMergedPercentages(nodes, anchors) {
  const step0 = anchors?.[0]?.step;
  if (step0 === undefined || step0 === null || !nodes.length) return nodes;
  const cohort = nodes.find((n) => n.isAnchor && n.step === step0)?.count ?? 0;
  if (cohort <= 0) return nodes;
  return nodes.map((n) => ({
    ...n,
    percentage: parseFloat((((n.count || 0) / cohort) * 100).toFixed(1)),
  }));
}

export function rebuildAnchorsFromMergedNodes(mergedNodes, rawAnchors) {
  if (!rawAnchors?.length) return rawAnchors || [];
  return rawAnchors.map((a, idx) => {
    const safe = String(a.eventName || "").replace(/[^a-zA-Z0-9]/g, "_");
    const id = `step_${a.step}__${safe}`;
    const mn = mergedNodes.find((n) => n.id === id && n.isAnchor);
    if (!mn) return a;
    const out = { ...a, count: mn.count };
    const lastIdx = rawAnchors.length - 1;
    if (
      idx === lastIdx &&
      rawAnchors[0] &&
      (a.convertedCount !== undefined || a.conversionRate !== undefined)
    ) {
      const safe0 = String(rawAnchors[0].eventName || "").replace(/[^a-zA-Z0-9]/g, "_");
      const id0 = `step_${rawAnchors[0].step}__${safe0}`;
      const m0 = mergedNodes.find((n) => n.id === id0 && n.isAnchor);
      const denom = m0?.count || 0;
      out.convertedCount = mn.count;
      out.conversionRate = denom > 0 ? parseFloat((((mn.count / denom) * 100)).toFixed(2)) : 0;
    }
    return out;
  });
}

export function mergeCohortLinks(cohortOrder, dataByCohort) {
  const map = new Map();
  const key = (l) => `${l.source}::${l.target}`;
  const emptySeg = () => Object.fromEntries(cohortOrder.map((c) => [c, 0]));

  for (const cohortName of cohortOrder) {
    const linkList = dataByCohort[cohortName]?.links || [];
    for (const l of linkList) {
      const k = key(l);
      const ex = map.get(k);
      if (!ex) {
        map.set(k, {
          ...l,
          segmentValues: { ...emptySeg(), [cohortName]: l.value },
          value: l.value,
        });
      } else {
        ex.segmentValues[cohortName] = (ex.segmentValues[cohortName] || 0) + l.value;
        ex.value = cohortOrder.reduce((s, cn) => s + (ex.segmentValues[cn] || 0), 0);
      }
    }
  }
  return [...map.values()];
}
